import { BehaviorSubject, combineLatest, EMPTY, Observable, Subject } from 'rxjs';
import { TicketCreate } from '@shared/models/ticket-create';
import { ApiService } from '@shared/apis';
import { AuthService } from '@core/auth/auth.service';
import { SpinnerService } from '@shared/services/spinner.service';
import Utils from '@shared/utils/utils';
import { CONTENT_STEP } from '@routes/tickets/ticket-edit/contents/contents-steps';
import { finalize, map, startWith } from 'rxjs/operators';
import * as _ from 'lodash-es';
import { Basic } from '@shared/models/basic';
import { Customer } from '@shared/models/customer';
import { Product } from '@shared/models/product';
import { Sc4uInfo } from '@shared/models/sc4u-info';
import { Pricing } from '@shared/models/pricing';
import { Constants } from '@shared/constants/constants';
import Configs from '@shared/constants/configs';
import * as moment from 'moment';
import { ServiceCenter } from '@shared/models/ticket-info';
import { Company } from '@shared/enums/company';

let nextId = 0;

export class TicketService {
  data$ = new BehaviorSubject<TicketCreate>({});
  stepIndex$ = new BehaviorSubject<any>(0);
  isScroll$ = new Subject<any>();
  company;
  serviceId = 0;
  _data: TicketCreate = {};

  constructor(private authService: AuthService,
              private apiService: ApiService,
              private spinnerService: SpinnerService) {
    nextId++;
    this.serviceId = nextId;
    this.company = this.authService.getCurrentCompany();
  }

  _resetSerial = false;

  get resetSerial() {
    return this._resetSerial;
  }

  set resetSerial(v) {
    this._resetSerial = v;
  }

  // <editor-fold defaultstate="collapsed" desc="# Getters & Setters">
  patch(value: TicketCreate = {}, callComponent, publish = true) {
    // console.log('patch::callComponent =', callComponent, ',this._data =', this._data);
    // console.log('patch::callComponent =', callComponent, ',value =', value);
    const mergedData: TicketCreate = { ...(this._data || {}), ...value };
    this.set(mergedData, callComponent, publish);
  }

  // </editor-fold>

  update(value: TicketCreate = {}, callComponent) {
    // console.log('update::: callComponent =', callComponent, ', value =', value);
    const mergedData: TicketCreate = { ...(this._data || {}), ...value };
    // console.log('update::: callComponent =', callComponent, ', mergedData =', mergedData);

    // check if value changed
    if (_.isEqual(this._data, mergedData)) {
      // not changed
      return;
    }

    this._data = mergedData;
    this.data$.next(mergedData);
    // console.log(`### UPDATE TicketService ID_${nextId} : [Call_${callComponent}] ==> `, this._data);
  }

  set(value: TicketCreate, callComponent, publish = true) {
    // console.log('set::: callComponent =', callComponent);
    if (!this._data) {
      this._data = {};
    }

    // review data and update related fields
    // reset related fields
    let keepFields = [
      'step',
      'basic',
      'customer',
      'product',
      'pricing',
      'symptoms',
      'serviceCenters',
      'serviceCenter',
      'sc4uInfo',
      'serverFile',
      'attachFiles',
      'inquiry',
      'attachment',
      'scheduleDate',
    ];

    const changed = keepFields.reduce((ret, key) => {
      // console.log(`key = ${key}, this._data[${key}] = ${this._data[key]}`);
      // check if value changed
      if (JSON.stringify(this._data[key] || null) === JSON.stringify((value || {})[key] || null)) {
        // not changed
        return ret;
      }

      if (key === 'basic') {
        keepFields = keepFields.filter(x => ![
          'customer', 'product', 'pricing', 'symptoms',
          'sc4uInfo', 'serverFile', 'serviceCenters', 'serviceCenter'
        ].includes(x));
      }

      if (key === 'customer') {
        keepFields = keepFields.filter(x => ![
          'product', 'pricing', 'symptoms',
          'sc4uInfo', 'serverFile', 'serviceCenters', 'serviceCenter', 'optOut'
        ].includes(x));
      }

      if (key === 'product') {
        keepFields = keepFields.filter(x => ![
          'symptoms', 'pricing', 'sc4uInfo',
          'serverFile', 'serviceCenters', 'serviceCenter'
        ].includes(x));
      }

      if (key === 'symptoms') {
        keepFields = keepFields.filter(x => !['pricing', 'serviceCenters', 'serviceCenter', 'scheduleDate'].includes(x));
      }

      if (key === 'serviceCenter') {
        keepFields = keepFields.filter(x => !['serviceCenter', 'scheduleDate'].includes(x));
      }

      // update field and return true for saving
      this._data[key] = (value || {})[key];
      if (!keepFields.includes(key)) {
        keepFields.push(key);
      }
      // console.log('>>>>>>>>>>>>>>>> key =', key, ', keepFields =', keepFields);

      return true;
    }, false);

    // remove fields other than keep
    this._data = keepFields.reduce((ret, field) => {
      if (!Utils.isEmpty(this._data[field])) {
        ret[field] = this._data[field];
      }
      return ret;
    }, {});

    // console.log('# this._data >>>>>>>>>> ', this._data);
    // console.log('# changed >>>>>>>>>> ', changed);

    if (changed) {
      this._data = Utils.trimObject(this._data);    // trim it 'cause i like clean

      if (publish) {
        // tell everybody it's changed
        this.data$.next(this._data);
        // console.log(' publish =================== ', this._data);
        // this.scrollToBottom();
      }
      // console.log(`### PATCH TicketService ID_${ nextId } : [Call_${ callComponent }] ==> `, this._data);
    }

  }

  // </editor-fold>

  // <editor-folder defaultstate="collapsed" desc="apis">

  // <editor-fold defaultstate="collapsed" desc="# step navigator">
  goNext(nextStep: CONTENT_STEP) {
    const isEditable = this.isEditable(nextStep);
    // check current step is done
    // console.log('isEditable =', isEditable);
    if (!isEditable) {
      return;
    }
    this.stepIndex$.next(nextStep);
  }

  isEditable(step: CONTENT_STEP) {
    // console.log('step =', step);
    // console.log('step =', step, ', this._data =', this._data);
    if (!this._data) {
      return false;
    }

    const isOW = this._data.product?.warrantyTerm === 'OW';
    const storeType = [Constants.serviceCode.DEMO_SERVICE, Constants.serviceCode.STOCK_REPAIR].includes(this._data.basic?.serviceType);
    const sc4uTargetAndSymptoms = this._data.sc4uInfo?.sc4uTarget && this._data.symptoms?.length;

    const owAndSc4uTarget = isOW && this._data.sc4uInfo?.sc4uTarget;
    const owAndNotSc4uTarget = isOW && !this._data.sc4uInfo?.sc4uTarget;
    const lpAndNotSc4uTarget = !isOW && !this._data.sc4uInfo?.sc4uTarget;

    const isSymptomsLength = !!this._data.symptoms?.length;

    const lpAndSymptoms = !isOW && isSymptomsLength;
    const lpAndSc4uTargetAndSymptoms = !isOW && sc4uTargetAndSymptoms;
    const lpAndNotSc4uTargetAndSymptoms = lpAndNotSc4uTarget && isSymptomsLength;

    const owAndNotSc4uTargetAndSymptoms = owAndNotSc4uTarget && isSymptomsLength;
    const owAndNotSc4uTargetAndNotSymptoms = owAndNotSc4uTarget && !isSymptomsLength;
    const owAndSc4uTargetAndSymptomsAndPricing = isOW && sc4uTargetAndSymptoms && !!this._data.pricing;

//     console.log('>>>>>>>>>>> isEditable:]\n' +
//       `- isOW = ${isOW}\n- storeType = ${storeType}\n- sc4uTargetAndSymptoms = ${sc4uTargetAndSymptoms}\n\n
// - owAndSc4uTarget = ${owAndSc4uTarget}\n- owAndNotSc4uTarget = ${owAndNotSc4uTarget}\n
// - lpAndNotSc4uTarget = ${lpAndNotSc4uTarget}\n\n- isSymptomsLength = ${isSymptomsLength}\n\n
// - lpAndSymptoms = ${lpAndSymptoms}\n- lpAndSc4uTargetAndSymptoms = ${lpAndSc4uTargetAndSymptoms}\n
// - lpAndNotSc4uTargetAndSymptoms = ${lpAndNotSc4uTargetAndSymptoms}\n\n
// - owAndNotSc4uTargetAndSymptoms = ${owAndNotSc4uTargetAndSymptoms}\n
// - owAndNotSc4uTargetAndNotSymptoms = ${owAndNotSc4uTargetAndNotSymptoms}\n
// - owAndSc4uTargetAndSymptomsAndPricing = ${owAndSc4uTargetAndSymptomsAndPricing}`)

    const symptomsCheck = (): boolean => {
      // console.log('### this._data.product?.serialNo = ', this._data.product?.serialNo);
      // console.log('### this._data.product?.modelCode = ', this._data.product?.modelCode);
      // console.log('### this._data.basic?.serviceType = ', this._data.basic?.serviceType);
      // console.log('### owAndNotSc4uTarget = ', owAndNotSc4uTarget);
      if (!this._data.product?.serialNo || !this._data.product?.modelCode) {
        return false;
      }
      return !(this._data.basic?.serviceType === Constants.serviceCode.PICKUP_SERVICE && owAndNotSc4uTarget);
    };

    const scheduleCheck = (): boolean => {
      const serviceType = this._data.basic?.serviceType;
      let rs: any = false;
      switch (serviceType) {
        case Constants.serviceCode.IN_HOME:
        case Constants.serviceCode.COSMETIC_REPAIR:
        case Constants.serviceCode.INSTALL_CHECKING:
          rs = lpAndSymptoms || owAndSc4uTargetAndSymptomsAndPricing || owAndNotSc4uTargetAndSymptoms;
          break;
        case Constants.serviceCode.PICKUP_SERVICE:
          rs = lpAndSc4uTargetAndSymptoms || owAndSc4uTargetAndSymptomsAndPricing || owAndNotSc4uTargetAndNotSymptoms || lpAndNotSc4uTargetAndSymptoms;
          break;
        case Constants.serviceCode.DEMO_SERVICE:
        case Constants.serviceCode.STOCK_REPAIR:
          rs = isSymptomsLength;
          break;
        default:
          break;
      }
      return rs;
    };

    switch (step) {
      case CONTENT_STEP.BASIC:
      case CONTENT_STEP.CUSTOMER:
        return true;
      case CONTENT_STEP.PRODUCT:
        const isProductEditable = !!this._data.customer && !!this._data.customer?.address?.zipcode;
        // console.log('isProductEditable =', isProductEditable);
        return isProductEditable;
      case CONTENT_STEP.SYMPTOMS:
        const isSymptomCheck = symptomsCheck();
        // console.log('isSymptomCheck =', isSymptomCheck);
        return isSymptomCheck
      case CONTENT_STEP.PRICING:
        return !storeType && isOW && this._data.pricing ||
          !storeType && isOW && sc4uTargetAndSymptoms
          || this._data.symptoms?.length && this._data.pricing?.amount > 0;
      case CONTENT_STEP.SCHEDULE:
        const isScheduleCheck = scheduleCheck();
        // console.log('isScheduleCheck =', isScheduleCheck);
        return isScheduleCheck;
      default:
        return false;
    }
  }

  setTypes(isDetail, serviceType, detailType, status, dealerPortalServiceTypes, processTypes) {
    console.log('isDetail =', isDetail, '\nserviceType =', serviceType, '\ndetailType =', detailType, '\nstatus =', status, '\ndealerPortalServiceTypes =', dealerPortalServiceTypes, '\nprocessTypes =', processTypes);
    let serviceOptions = [];
    if (isDetail) {
      // console.log('serviceType =', serviceType);
      Object.keys(serviceType).forEach(key => {
        serviceOptions.push({
          code: serviceType[key].code,
          name: serviceType[key].name,
        });
      });
    } else {
      console.log('dealerPortalServiceTypes =', dealerPortalServiceTypes);
      Object.keys(dealerPortalServiceTypes).forEach(key => {
        serviceOptions.push({
          code: dealerPortalServiceTypes[key].code,
          name: dealerPortalServiceTypes[key].name,
          customerType: dealerPortalServiceTypes[key].customerType,
        });
      });
    }

    let detailOptions = [];
    Object.keys(detailType).forEach(key => {
      detailOptions.push({
        code: detailType[key].code,
        name: detailType[key].name,
      });
    });

    let statusOptions = [];
    let reasonOptions = [];
    Object.keys(status).forEach(key => {
      statusOptions.push({
        code: key,
        name: status[key].name,
        processType: status[key].processType,
      });

      if (!Utils.isEmpty(status[key].reason)) {
        Object.keys(status[key].reason).forEach(rs => {
          reasonOptions.push({
            statusCode: key,
            code: status[key].reason[rs].code,
            name: status[key].reason[rs].name,
          });
        });
      }
    });

    let processTypeOptions = [];
    Object.keys(processTypes).forEach(key => {
      processTypeOptions.push({
        code: key,
        name: processTypes[key],
      });
    });

    return {
      serviceTypes: serviceOptions,
      detailTypes: detailOptions,
      statusList: statusOptions,
      reasonList: reasonOptions,
      cloneReasonList: _.cloneDeep(reasonOptions),
      processTypes: processTypeOptions
    };
  }

  serialInfo(serialInfo) {
    // console.log('>>>>>>>>>>>> serialInfoserialInfo =', this._data);
    // this._data.product = null;
    // this.patch(this._data, 'TicketService :: serialInfo');
    const { basic } = this._data;
    // console.log('# basic ============================== ', basic);

    this.spinnerService.start();
    this.apiService.product.getSerialInfo(serialInfo)
      .pipe(finalize(() => this.spinnerService.stop()))
      .subscribe(res => {
        const productInfo = res;

        const { sc4u, checkHrm, sc4uObjectId, modelCode, categoryId, categoryIdNum, warrantyTerm } = productInfo;
        const { serviceType } = this._data.basic || {};

        delete productInfo.purchaseDate // delete purchaseDate

        let data: any = { product: productInfo };

        if (!modelCode) {
          this.patch(data, 'TicketService :: serialInfo');
          return;
        }

        data = {
          ...data,
          sc4uInfo: {
            sc4uObjectId,
            sc4uTarget: sc4u,
            checkHrm,
          },
        };

        if ([Constants.serviceCode.IN_HOME, Constants.serviceCode.PICKUP_SERVICE].includes(serviceType)) {
          const modelCheck = this.psModelCheck(serviceType, categoryId, modelCode, categoryIdNum);
          basic.serviceType = modelCheck ? Constants.serviceCode.PICKUP_SERVICE : Constants.serviceCode.IN_HOME;
          if (serviceType !== basic.serviceType) {
            basic.isPsModel = basic.serviceType === Constants.serviceCode.PICKUP_SERVICE;
            data = {
              ...basic,
              ...productInfo,
            };
          }
        }

        if (basic.serviceType === Constants.serviceCode.PICKUP_SERVICE && warrantyTerm === 'OW' && !sc4u) {
          data.symptoms = [];
        }

        // console.log('# serialInfo data = > ', data)

        this.patch(data, 'TicketService :: after getData using serialInfo');
        let nextIndex = basic.serviceType === Constants.serviceCode.PICKUP_SERVICE && warrantyTerm === 'OW' && !sc4u ? CONTENT_STEP.SCHEDULE : CONTENT_STEP.SYMPTOMS;
        // console.log('nextIndex =', nextIndex);
        this.goNext(nextIndex);
      }, err => Utils.showError(err));
  }

  psModelCheck = (serviceType, categoryId, modelCode, categoryIdNum): boolean => {
    // console.log(`>> psModelCheck:\n- serviceType = ${ serviceType }\n- categoryId = ${ categoryId }\n- modelCode = ${ modelCode }\n- categoryIdNum = ${ categoryIdNum }`);
    if (![Constants.serviceCode.PICKUP_SERVICE, Constants.serviceCode.IN_HOME].includes(serviceType)) {
      return false;
    }

    if (this.company === Company.SeaIm) {
      return true;
    }

    let isPsModel = ['MON', 'VDE'].includes(categoryId);
    if ('VDE' === categoryId) {
      isPsModel = modelCode.substring(2, 4) <= 43;
    }

    const psLocalNums = ['09', '10', '30', '40', '46', '95', '96'];
    if (psLocalNums.includes(categoryIdNum)) {
      isPsModel = true;
    }

    return isPsModel;
  };

  saveTicket = async (inquiry) => {
    const {
      basic,
      customer,
      product,
      symptoms,
      pricing,
      serviceCenter,
      sc4uInfo,
      serverFile,
      attachFiles,
    } = this._data;


    let params: any = {
      basic,
      customer,
      product,
      pricing,
      serviceCenter,
      sc4uInfo,
      serverFile,
      attachFiles,
      symptoms,
      inquiry
    };


    // console.log('param =', params);

    if (serviceCenter.ascNo === Constants.specialAsc.NO_ASC.ascNo) {
      params.basic.status = 'ST005';
      params.basic.reason = '';

      const hqDate = moment().add(7, 'd').format(Configs.format.serverLocalDate);
      params.serviceCenter.preferredDate = moment(`${ hqDate } ${ Configs.format.initTime }`).format(Configs.format.serverISODateTime);
    } else {
      params = this.setParamsServiceCenter(params, serviceCenter);
    }

    return await this.apiService.ticket.saveTicket(params).toPromise();
  };

  async updateTicket() {
    console.log('this._data =', this._data);
    const {
      basic,
      serviceCenter,
      scheduleDate,
      symptoms,
      pricing,
      product,
      serverFile,
    } = this._data;

    let params: any = {
      ticketNo: basic.ticketNo,
      serviceType: basic.serviceType,
      ascNo: serviceCenter.ascNo,
      status: basic.status,
      reason: basic.reason,
      product,
      scheduleDate,
      serviceCenter,
      serverFile,
      symptoms: symptoms?.map(symptom => symptom.code),
      pricing,
    };
    if (!scheduleDate) {
      if (serviceCenter.ascNo === Constants.specialAsc.NO_ASC.ascNo) {
        if (basic.processType === Constants.processType.SO) {
          params.status = 'ST005';
          params.reason = '';
        }

        const hqDate = moment().add(7, 'd').format(Configs.format.serverLocalDate);
        params.serviceCenter.preferredDate = moment(`${ hqDate } ${ Configs.format.initTime }`).format(Configs.format.serverISODateTime);
      } else {
        params = this.setParamsServiceCenter(params, serviceCenter);
      }
    }
    // return;
    return await this.apiService.ticket.saveTicket(params).toPromise();
  }

  async cancelDraftTicket() {
    const {
      basic,
    } = this._data;
    let params: any = {
      ticketNo: basic.ticketNo,
    };
    // return;
    return await this.apiService.ticket.cancelDraftTicket(params).toPromise();
  }

  async approve5kTicket(status, reason) {
    const {
      basic,
      serviceCenter,
      scheduleDate,
      symptoms,
      pricing,
    } = this._data;

    let params: any = {
      ticketNo: basic.ticketNo,
      serviceType: basic.serviceType,
      ascNo: serviceCenter.ascNo,
    };

    params.status = status;
    params.reason = reason;

    return await this.apiService.ticket.saveTicket(params).toPromise();
  }

  async reject5kTicket(reason) {
    const {
      basic,
      serviceCenter,
      scheduleDate,
      symptoms,
      pricing,
    } = this._data;

    let params: any = {
      ticketNo: basic.ticketNo,
      serviceType: basic.serviceType,
      ascNo: serviceCenter.ascNo,
      status: Constants.gcicStatus.SR_CANCEL.code,
      reason: reason.reason,
      rejectComment: reason.comment,
    };
    // console.log('reject5kTicket:::params =', params);

    return await this.apiService.ticket.saveTicket(params).toPromise();
  }

  // </editor-fold>

  setParamsServiceCenter(params, serviceCenter) {
    if (serviceCenter?.schedule) {
      const date = serviceCenter.schedule.date;
      if (serviceCenter.schedule.key) {
        const time = Configs.timeSlot[serviceCenter.schedule.key.toUpperCase()].start;
        params.serviceCenter.preferredDate = moment(`${ date } ${ time }`).format(Configs.format.serverISODateTime);
      } else {
        params.serviceCenter.preferredDate = moment(`${ date } ${ Configs.format.initTime }`).format(Configs.format.serverISODateTime);
      }
    } else {
      const hqDate = moment().add(3, 'd').format(Configs.format.serverLocalDate);
      params.serviceCenter.preferredDate = moment(`${ hqDate } ${ Configs.format.initTime }`).format(Configs.format.serverISODateTime);
    }
    return params;
  }

  setDetailData(ticketInfo, sync = false) {
    const basic: Basic = {
      ticketNo: ticketInfo.ticketNo,
      customerType: ticketInfo.customerType || 'Customer',
      serviceType: ticketInfo.serviceType,
      isPsModel: ticketInfo.serviceType === 'PS',
      detailType: ticketInfo.detailType,
      status: ticketInfo.status,
      reason: ticketInfo.reason,
      postingDate: ticketInfo.postingDate,
      lastUpdateDate: ticketInfo.lastUpdateDate || '',
      completeDate: ticketInfo.completeDate,
      detailDescription: ticketInfo.detailDescription,
      displayUnitRepairType: ticketInfo.displayUnitRepairType,
      createdBy: ticketInfo.auth?.createdBy?.username || '',
      processType: ticketInfo.processType,
    };

    const customer: Customer = {
      firstName: ticketInfo.firstName,
      lastName: ticketInfo.lastName,
      bpNo: ticketInfo.bpNo,
      address: ticketInfo.address,
      cellPhone: ticketInfo.cellPhone,
      homePhone: ticketInfo.homePhone,
      workPhone: ticketInfo.workPhone,
      workExtension: ticketInfo.workExtension || '',
      email: ticketInfo.email,
      optOut: ticketInfo?.optOut,
    };

    const product: Product = {
      serialNo: ticketInfo.serialNo,
      modelCode: ticketInfo.modelCode,
      demoUnit: ticketInfo.demoUnit,
      fmr: ticketInfo.fmr,
      imei: ticketInfo.imei,
      productType: ticketInfo.productType,
      productName: ticketInfo.productName,
      groupName: ticketInfo.groupName,
      modelImageUrl: ticketInfo.modelImageUrl,
      warrantyType: ticketInfo.warrantyType,
      warrantyStatus: ticketInfo.warrantyStatus,
      warrantyTerm: ticketInfo.warrantyTerm,
      warrantyException: ticketInfo.warrantyException,
      laborValidDateTo: ticketInfo.laborValidDateTo,
      redo: ticketInfo.redo,
      partValidDateTo: ticketInfo.partValidDateTo,
      manufacturerDate: ticketInfo.manufacturerDate,
      purchaseDate: ticketInfo.purchaseDate,
      receipt: ticketInfo.receipt,
      categoryIdNum: ticketInfo.categoryIdNum,
    };

    const symptoms = ticketInfo.symptomDescriptions;
    const scheduleDate = ticketInfo.scheduleDate;

    const sc4uInfo: Sc4uInfo = {
      sc4uObjectId: ticketInfo?.sc4u?.sc4uObjectId,
      sc4uTarget: ticketInfo?.sc4u?.sc4uTarget || false,
      checkHrm: ticketInfo?.sc4u?.checkHrm,
      dialogCommunications: ticketInfo?.sc4u?.dialogCommunications,
      dialogParts: ticketInfo?.sc4u?.dialogParts,
      pricing: ticketInfo?.sc4u?.pricing,
    };

    const pricing: Pricing = {
      amount: ticketInfo.cardHoldInfo?.amount || 0,
      status: ticketInfo.cardHoldInfo?.status,
    };

    const serviceCenter: ServiceCenter = {
      ascNo: ticketInfo.ascNo,
    };

    const data: TicketCreate = {
      basic,
      customer,
      product,
      symptoms,
      sc4uInfo,
      pricing,
      scheduleDate,
      serviceCenter,
      inquiry: ticketInfo.customerInquiry,
    };

    setTimeout(() => {
      this.update(data, 'setDetailData');
    });
  }

  // <editor-folder defaultstate="collapsed" desc="common">
  scrollToBottom() {
    setTimeout(() => {
      this.isScroll$.next(true);
    }, 100);
  }

  formValidation(form): Observable<any> {
    const validForm = [];

    Object.keys(form.controls).forEach(key => {
      const errors = form.controls[key].errors;
      const status = form.controls[key].status;

      if (!errors || status === 'DISABLED') {
        return;
      }

      validForm.push(
        form.controls[key].valueChanges.pipe(startWith(''))
      );
    });

    if (!validForm.length) {
      return EMPTY;
    }

    return combineLatest(validForm)
      .pipe(
        map(res => {
          const filter = res.filter(f => !f);
          return !filter.length;
        })
      );
  }

  fromInputValue(form, isValid = false, key) {
    const formValue = form.getRawValue();

    const params = {};
    if (Utils.isNullObj(formValue)) {
      return;
    }

    params[key] = formValue;
    this.patch(params, 'TicketService :: fromInputValue');
  }

  // </editor-fold>

}
